import Grid from '@mui/material/Grid';
import { Box, Stack } from '@mui/material';
import { useFamilyIdPermissions } from '../../Model/SessionModel';
import {
  Arrangement,
  CombinedFamilyInfo,
  Permission,
  ArrangementPhase,
} from '../../GeneratedClient';
import { useV1CasesModel } from '../../Model/V1CasesModel';
import { DateDisplayEditor } from './DateDisplayEditor';
import { useBackdrop } from '../../Hooks/useBackdrop';
import { format } from 'date-fns';

interface ArrangementPlannedDurationProps {
  partneringFamily: CombinedFamilyInfo;
  v1CaseId: string;
  arrangement: Arrangement;
  summaryOnly?: boolean;
  cancelButton?: React.ReactNode;
  startButton?: React.ReactNode;
  endButton?: React.ReactNode;
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <Grid
      size={1}
      sx={{
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 0,
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      {children}
    </Grid>
  );
}

export function ArrangementPlannedDuration({
  partneringFamily,
  v1CaseId,
  arrangement,
  summaryOnly,
}: ArrangementPlannedDurationProps) {
  const partneringFamilyId = partneringFamily.family!.id!;
  const permissions = useFamilyIdPermissions(partneringFamilyId);
  const v1CasesModel = useV1CasesModel();

  const withBackdrop = useBackdrop();

  const onDateChange = async <TDate extends Date | null>(
    callback: (
      aggregateId: string,
      v1CaseId: string,
      arrangementId: string,
      plannedStartLocal: TDate
    ) => Promise<void>,
    newDate: TDate
  ) =>
    await withBackdrop(async () => {
      await callback(partneringFamilyId, v1CaseId, arrangement.id!, newDate);
    });

  const canEdit = !summaryOnly && permissions(Permission.EditArrangement);

  if (summaryOnly) {
    return (
      <Stack direction="column" sx={{ clear: 'both' }}>
        <Box>
          <span>Planned start:&nbsp;</span>
          {arrangement.plannedStartUtc
            ? format(arrangement.plannedStartUtc, 'M/d/yyyy')
            : '-'}
        </Box>
        <Box>
          <span>Planned end:&nbsp;</span>
          {arrangement.plannedEndUtc
            ? format(arrangement.plannedEndUtc, 'M/d/yyyy')
            : '-'}
        </Box>
      </Stack>
    );
  }

  return (
    <Grid
      container
      spacing={0}
      columns={{ xs: 1, sm: 2 }}
      sx={{
        clear: 'both',
        mt: 2,
        mb: 1,
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Cell>
        <DateDisplayEditor
          label="Requested at"
          initialValue={arrangement.requestedAtUtc!}
          canEdit={canEdit}
          availableInCurrentPhase // Available in all phases
          onChange={(newDate) =>
            onDateChange(v1CasesModel.editArrangementRequestedAt, newDate)
          }
        />
      </Cell>

      <Cell>
        <DateDisplayEditor
          label="Cancelled at"
          initialValue={arrangement.cancelledAtUtc!}
          canEdit={canEdit}
          availableInCurrentPhase={
            arrangement.phase === ArrangementPhase.Cancelled
          }
          unavailableTooltip="Only available when the arrangement is cancelled"
          onChange={(newDate) =>
            onDateChange(v1CasesModel.editArrangementCancelledAt, newDate)
          }
        />
      </Cell>

      <Cell>
        <DateDisplayEditor
          label="Planned start"
          initialValue={arrangement.plannedStartUtc!}
          disableFuture={false}
          canEdit={canEdit}
          availableInCurrentPhase // Available in all phases
          allowClear
          onChange={(newDate) =>
            onDateChange(v1CasesModel.planArrangementStart, newDate)
          }
        />
      </Cell>

      <Cell>
        <DateDisplayEditor
          label="Started at"
          initialValue={arrangement.startedAtUtc!}
          canEdit={canEdit}
          availableInCurrentPhase={
            (arrangement.phase || 0) >= ArrangementPhase.Started
          }
          unavailableTooltip="Only available when the arrangement is started"
          onChange={(newDate) =>
            onDateChange(v1CasesModel.editArrangementStartTime, newDate)
          }
        />
      </Cell>

      <Cell>
        <DateDisplayEditor
          label="Planned end"
          initialValue={arrangement.plannedEndUtc!}
          disableFuture={false}
          canEdit={canEdit}
          availableInCurrentPhase // Available in all phases
          allowClear
          onChange={(newDate) =>
            onDateChange(v1CasesModel.planArrangementEnd, newDate)
          }
        />
      </Cell>

      <Cell>
        <DateDisplayEditor
          label="Ended at"
          initialValue={arrangement.endedAtUtc!}
          canEdit={canEdit}
          availableInCurrentPhase={arrangement.phase === ArrangementPhase.Ended}
          unavailableTooltip="Only available when the arrangement is ended"
          onChange={(newDate) =>
            onDateChange(v1CasesModel.editArrangementEndTime, newDate)
          }
        />
      </Cell>
    </Grid>
  );
}
